import * as React from 'react';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import {getApiTypeFromGraphQLTypename} from 'nutshell-core/utils';

import type {ChannelStatus, ThreadType} from 'nutshell-graphql-types';
import type {
    EventFragmentSparse_actor as Actor,
    NoteFragment_entity as Entity,
    EventFragmentSparse_payload_Thread_bestEntity as BestEntity,
    SubmissionFragment_formVersion_form as Form,
    EmailInteractionFragmentSparse as EmailInteraction,
    SessionFragment_url as Url,
    EmailFragmentSparse as Email,
} from 'nutshell-graphql-types';

import {Link} from '../../../link/link';
import {LinkedEntity} from '../../../linked-entity/linked-entity';

import type {Participant, EventType, ChangeType} from '../../types';

import {
    getActionFromChangeType,
    getTimelineHeaderVerb,
    getActorForDisplay,
    getParticipantListForHeader,
    isForPhoneIntegration,
} from './helpers';
import {TimelineHeaderParticipants} from './timeline-header-participants';

import styles from './timeline-header.module.css';

// TimelineHeaderActionTextProps
export type Props = {
    actor: Actor | BestEntity;
    participants?: Participant[] | null | undefined;
    entity?: Entity | null | undefined | Form | EmailInteraction | Email | Url;
    entryType: EventType;
    changeType: ChangeType;
    showEmailAddressTooltip?: boolean;
    actorEmailAddress?: string;
    numVisits?: number;
    isPinnedThread?: boolean;
    threadType?: ThreadType;
    externalChannelStatus?: ChannelStatus | null | undefined;
};

export function TimelineHeaderActionText(props: Props) {
    const {
        actor,
        participants,
        entity,
        entryType,
        changeType,
        showEmailAddressTooltip,
        actorEmailAddress,
        numVisits,
        isPinnedThread,
        threadType,
        externalChannelStatus,
    } = props;

    const containerRef = React.useRef(null);
    const actorVerbRef = React.useRef(null);

    const action = getActionFromChangeType(changeType);
    const actorForDisplay = getActorForDisplay(entryType, actor, participants || []);
    const participantsForHeader = getParticipantListForHeader(participants, actorForDisplay);
    const hasParticipants = participantsForHeader.length > 0;
    // For now, we only care if phone calls are autologged or not
    const isAutologged = actor.__typename === 'Origin' && isForPhoneIntegration(entryType);

    React.useEffect(() => {
        ReactTooltip.rebuild();

        return () => ReactTooltip.hide();
    }, []);

    const getEntityToDisplay = (
        entityToDisplay: Entity | Form | EmailInteraction | Email | Url
    ) => {
        if (entityToDisplay.__typename === 'Url') {
            return (
                <span>
                    {entityToDisplay.host
                        ? entityToDisplay.host
                        : entityToDisplay.fullyQualifiedUrl}
                </span>
            );
        }

        if (entityToDisplay.__typename === 'Form') {
            return (
                <Link href={entityToDisplay.htmlUrlPath} variant='primary' newTab={true}>
                    {entityToDisplay.name}
                </Link>
            );
        }

        if (entityToDisplay.__typename === 'EmailInteraction') {
            if (entityToDisplay.emailDelivery && entityToDisplay.emailDelivery.edition) {
                const htmlUrlPath = entityToDisplay.emailDelivery.edition.htmlUrlPath;
                const name = entityToDisplay.emailDelivery.edition.name;

                if (entityToDisplay.type === 'CLICKED') {
                    return (
                        <>
                            {entityToDisplay.link ? (
                                <span>
                                    <Link href={entityToDisplay.link} variant='primary'>
                                        {entityToDisplay.link}
                                    </Link>
                                    &nbsp;from&nbsp;
                                </span>
                            ) : (
                                <span>a link from&nbsp;</span>
                            )}
                            <span>
                                <Link href={htmlUrlPath} variant='primary' newTab={true}>
                                    {name}
                                </Link>
                            </span>
                        </>
                    );
                } else {
                    return (
                        <Link href={htmlUrlPath} variant='primary' newTab={true}>
                            {name}
                        </Link>
                    );
                }
            }
        }

        if (entityToDisplay.__typename === 'Email') {
            const href = entityToDisplay.htmlUrlPath;
            // The title is the name of the edition but if we don't have that for some reason,
            // we default to the subject of the email
            const emailTitle = entityToDisplay.edition
                ? entityToDisplay.edition.name
                : entityToDisplay.subject;

            return (
                <Link href={href} newTab={true} variant='primary'>
                    {emailTitle}
                </Link>
            );
        }

        return (
            <LinkedEntity
                displayInline={true}
                allowWrap={true}
                shouldClientRoute={true}
                useEntityColor={true}
                type={getApiTypeFromGraphQLTypename(entityToDisplay.__typename)}
                greenLeads={true}
                {...entityToDisplay}
            />
        );
    };

    return (
        <div ref={containerRef} className={styles['action-text']}>
            <span ref={actorVerbRef}>
                <span
                    data-tip={
                        showEmailAddressTooltip && actorEmailAddress ? actorEmailAddress : undefined
                    }
                    data-place='bottom'
                    className={styles.actor}
                >
                    {actor ? (
                        <LinkedEntity
                            allowWrap={true}
                            shouldClientRoute={true}
                            displayInline={true}
                            useEntityColor={true}
                            type={getApiTypeFromGraphQLTypename(actor.__typename)}
                            {...actorForDisplay}
                        />
                    ) : (
                        'Unknown actor'
                    )}
                </span>
                <span className={styles.verb}>
                    &nbsp;
                    {getTimelineHeaderVerb({
                        type: entryType,
                        action,
                        hasParticipants,
                        numVisits,
                        isPinnedThread,
                        threadType,
                        externalChannelStatus,
                        isAutologged,
                    })}
                    &nbsp;
                </span>
            </span>
            {hasParticipants ? (
                <span className={styles.participants}>
                    <TimelineHeaderParticipants
                        participants={participantsForHeader}
                        containerRef={containerRef}
                        actorVerbRef={actorVerbRef}
                        showEmailAddressTooltip={showEmailAddressTooltip}
                    />
                </span>
            ) : undefined}
            {entity ? (
                <span className={styles.target}>{getEntityToDisplay(entity)}</span>
            ) : undefined}
        </div>
    );
}
